import { useLanguage } from "@/context/LanguageContext";
import { FLEET_SCHEMA } from "@/config/structuredData";
import { ServiceBookingOptions } from "@/components/ServiceBookingOptions";
import { STATIC_SERVICE_SEO } from "@/config/serviceSeo";
import { Seo } from "@/components/Seo";
import { SectionHeading, FleetCard, QuoteButton, Eyebrow } from "@/components/shared";
import { FLEET } from "@/config/fleet";
import { IMG } from "@/config/images";
import { Info } from "lucide-react";
import { Link } from "@/i18n/LocalizedLink";
import { VehicleTariffTable } from "@/components/VehicleTariffTable";
import { FareTerms } from "@/components/FareTerms";

export default function Fleet() {
  const { t, tParts } = useLanguage();
  return (
    <>
      <Seo
        title={t(STATIC_SERVICE_SEO.fleet.title)}
        description={t(STATIC_SERVICE_SEO.fleet.description)}
        jsonLd={FLEET_SCHEMA}
        image={IMG.innova}
      />
      <section className="bg-[#071426]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Eyebrow className="text-amber-400">{t("Our Fleet")}</Eyebrow>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">{t(STATIC_SERVICE_SEO.fleet.h1)}</h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300">{t("Choose a car, SUV or Tempo Traveller for your trip from Satna. Our taxi services cover smaller parties and groups; send your route and passenger count for car hire advice, cab booking or group vehicle availability.")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="mb-8 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="fleet-disclaimer">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p>{tParts("Photos illustrate the vehicle categories. Compare the published model rates below, then {quote_emphasis} to confirm availability, vehicle specifications, luggage space and the final fare for your itinerary.", {
            quote_emphasis: <strong key="quote">{t("Request a Quote")}</strong>,
          })}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2 lg:max-w-5xl lg:mx-auto">
          {FLEET.map((item) => <FleetCard key={item.id} item={item} />)}
        </div>
        <ServiceBookingOptions page="fleet" className="mt-12" />
        <div className="mt-14" id="vehicle-models">
          <SectionHeading
            eyebrow={t("Compare vehicle rates")}
            title={t("Car, SUV & Tempo Traveller rate list")}
            intro={t("Seating is shown as passengers plus one driver. Compare local and outstation hire, extra-hour and night charges, then select a vehicle for your trip quote. Confirm the itinerary and availability before booking.")}
          />
          <div className="mt-8"><VehicleTariffTable /></div>
          <div className="mt-8"><FareTerms /></div>
          <p className="mt-6 text-sm leading-relaxed text-slate-600">
            {tParts("For group travel, read our {tempo_link}. For pickup arrangements, see the {areas_link} covered from our Satna base.", {
              tempo_link: <Link key="tempo" to="/tempo-traveller/" className="font-semibold text-amber-700 underline underline-offset-4">{t("Tempo Traveller hire and rental details")}</Link>,
              areas_link: <Link key="areas" to="/service-areas/" className="font-semibold text-amber-700 underline underline-offset-4">{t("service locations")}</Link>,
            })}
          </p>
        </div>
        <div className="mt-10 text-center"><QuoteButton testId="fleet-cta-quote-button">{t("Request a Quote")}</QuoteButton></div>
      </section>
    </>
  );
}
